import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { createPortal } from "react-dom";
import MaterialReactTable from "material-react-table";
import { ExportToCsv } from "export-to-csv";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import lambdaFetch from "../functions/FetchFromLambda";

const { REACT_APP_STAGE } = process.env;

const csvOptions = {
  fieldSeparator: ",",
  quoteStrings: '"',
  decimalSeparator: ".",
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const csvExporter = new ExportToCsv(csvOptions);

const USERS_CONFIG = {
  name: "Users",
  endpoint: "users",
  tableName: `apix_${REACT_APP_STAGE}_sys_users`,
  primaryKey: "ID",
  description: "System users and their access configurations",
  editable: true,
  autoGenerateId: true,
  idPrefix: "USER_",
  customColumns: [
    { accessorKey: "username", header: "Username", size: 200 },
    { accessorKey: "long_name", header: "Long Name", size: 200 },
    { accessorKey: "roles", header: "App Roles", size: 200 },
    { accessorKey: "data_role", header: "Data Role", size: 200 },
    { accessorKey: "active", header: "Active", size: 100 },
  ],
};

function parseCsvLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') inQuotes = !inQuotes;
    else if ((c === "," && !inQuotes) || (c === "\t" && !inQuotes)) {
      result.push(current.trim());
      current = "";
    } else current += c;
  }
  result.push(current.trim());
  return result;
}

function normalizeBatchHeader(h) {
  const s = (h || "").toLowerCase().replace(/\s+/g, "_").replace(/"/g, "");
  if (s === "name" || s === "long_name" || s === "longname") return "long_name";
  if (s === "username" || s === "user") return "username";
  if (s === "roles" || s === "app_roles" || s === "approles") return "roles";
  if (s === "data_role" || s === "datarole") return "data_role";
  if (s === "active") return "active";
  return s;
}

const DEFAULT_APP_ROLE = "default";

/** Normalize roles to a trimmed string array for form state (ensures chips match options and can be removed). */
function normalizeRolesToArray(roles) {
  if (roles == null || roles === "") return [];
  if (Array.isArray(roles)) return roles.map((r) => String(r).trim()).filter(Boolean);
  return String(roles)
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
}

/** Ensure the default app role is always in the list (users must always have at least default). */
function ensureDefaultInRoles(rolesArray) {
  const normalized = Array.isArray(rolesArray) ? rolesArray.map((r) => String(r).trim()).filter(Boolean) : [];
  const hasDefault = normalized.some((r) => r.toLowerCase() === DEFAULT_APP_ROLE.toLowerCase());
  if (hasDefault) return normalized;
  return [DEFAULT_APP_ROLE, ...normalized];
}

function parseBatchUploadCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return { rows: [], errors: ["Need a header row and at least one data row."] };
  const headerRow = parseCsvLine(lines[0]);
  const headers = headerRow.map(normalizeBatchHeader);
  const rows = [];
  const errors = [];
  const seenUsernames = new Set();
  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] !== undefined ? String(values[idx] || "").replace(/^"|"$/g, "").trim() : "";
    });
    const username = (row.username || "").trim();
    if (!username) errors.push(`Row ${i + 1}: Username is required.`);
    else if (seenUsernames.has(username.toLowerCase())) errors.push(`Row ${i + 1}: Duplicate username "${username}".`);
    else seenUsernames.add(username.toLowerCase());
    const rolesStr = (row.roles || "").trim();
    const roleList = rolesStr ? rolesStr.split(/[,;|]/).map((r) => r.trim().toLowerCase()) : [];
    if (roleList.includes("super_admin")) errors.push(`Row ${i + 1}: Role "super_admin" is not allowed.`);
    rows.push(row);
  }
  return { rows, errors };
}


export default function UsersTable({ usersData = [], loading, error, credentials, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [currentItem, setCurrentItem] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemPendingDelete, setItemPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [availableAppRoles, setAvailableAppRoles] = useState([]);
  const [availableDataRoles, setAvailableDataRoles] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [batchUploadRows, setBatchUploadRows] = useState([]);
  const [batchUploadErrors, setBatchUploadErrors] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchUploadProgress, setBatchUploadProgress] = useState({ current: 0, total: 0 });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const fetchAvailableDataRoles = useCallback(async () => {
    try {
      const endpoint = "dynamo/roles/scan";
      const requestBody = {
        limit: 1000,
        filterExpression: "#active = :active AND #type = :type",
        expressionAttributeNames: { "#active": "active", "#type": "type" },
        expressionAttributeValues: { ":active": true, ":type": "data" },
      };
      const response = await lambdaFetch(endpoint, "post", credentials.user.accessToken, JSON.stringify(requestBody), "", credentials);
      let roles = [];
      if (response?.data) {
        if (Array.isArray(response.data)) roles = response.data;
        else if (response.data.body) {
          const bodyObj = typeof response.data.body === "string" ? JSON.parse(response.data.body) : response.data.body;
          const finalData = typeof bodyObj?.data === "string" ? JSON.parse(bodyObj.data) : bodyObj?.data;
          roles = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) roles = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        setAvailableDataRoles(roles.map((r) => r.role_name).filter((n) => n != null && n !== ""));
      } else setAvailableDataRoles([]);
    } catch (err) {
      console.error("Error fetching data roles:", err);
      setAvailableDataRoles([]);
    }
  }, [credentials]);

  const fetchAvailableAppRoles = useCallback(async () => {
    try {
      const endpoint = "dynamo/roles/scan";
      const requestBody = {
        limit: 1000,
        filterExpression: "#active = :active AND #type = :type",
        expressionAttributeNames: { "#active": "active", "#type": "type" },
        expressionAttributeValues: { ":active": true, ":type": "app" },
      };
      const response = await lambdaFetch(endpoint, "post", credentials.user.accessToken, JSON.stringify(requestBody), "", credentials);
      let roles = [];
      if (response?.data) {
        if (Array.isArray(response.data)) roles = response.data;
        else if (response.data.body) {
          const bodyObj = typeof response.data.body === "string" ? JSON.parse(response.data.body) : response.data.body;
          const finalData = typeof bodyObj?.data === "string" ? JSON.parse(bodyObj.data) : bodyObj?.data;
          roles = Array.isArray(finalData) ? finalData : finalData ? [finalData] : [];
        } else if (response.data.data) roles = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        setAvailableAppRoles(roles.map((r) => r.role_name).filter((n) => n != null && n !== ""));
      } else setAvailableAppRoles([]);
    } catch (err) {
      console.error("Error fetching app roles:", err);
      setAvailableAppRoles([]);
    }
  }, [credentials]);

  const handleAddClick = async () => {
    setDialogMode("create");
    setEditingItem(null);
    setCurrentItem({ active: true, roles: [DEFAULT_APP_ROLE] });
    await Promise.all([fetchAvailableAppRoles(), fetchAvailableDataRoles()]);
    setDialogOpen(true);
  };

  const handleEditClick = async (item) => {
    setDialogMode("edit");
    setCurrentItem({
      ...item,
      roles: ensureDefaultInRoles(normalizeRolesToArray(item.roles)),
    });
    setEditingItem(item);
    await Promise.all([fetchAvailableAppRoles(), fetchAvailableDataRoles()]);
    setDialogOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemPendingDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    setDialogOpen(false);
    setCurrentItem({});
    setEditingItem(null);
  };

  const handleInputChange = (field, value) => {
    setCurrentItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const config = USERS_CONFIG;
    try {
        if (dialogMode === "create") {
        let itemToCreate = { ...currentItem };
        if (config.autoGenerateId && !itemToCreate[config.primaryKey]) {
          itemToCreate[config.primaryKey] = `${config.idPrefix}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        const createRoles = ensureDefaultInRoles(
          itemToCreate.roles ? normalizeRolesToArray(itemToCreate.roles) : []
        );
        itemToCreate.roles = createRoles.join(", ");
        const response = await lambdaFetch(`dynamo/${config.endpoint}`, "post", credentials.user.accessToken, JSON.stringify(itemToCreate), "Item created successfully", credentials);
        if (response?.success) {
          handleDialogClose();
          onRefresh();
          showSnackbar("User created successfully");
        }
      } else {
        const primaryKeyValue = editingItem[config.primaryKey];
        const updates = { ...currentItem };
        delete updates[config.primaryKey];
        delete updates.created;
        delete updates.updated;
        delete updates.id;
        const currentUser = credentials.user;
        const isSelfEdit =
          (currentUser.username && editingItem.username && currentUser.username === editingItem.username) ||
          (currentUser.ID && editingItem.ID && currentUser.ID === editingItem.ID) ||
          (currentUser.id && editingItem.ID && currentUser.id === editingItem.ID) ||
          (currentUser.email && editingItem.username && currentUser.email === editingItem.username);
        if (isSelfEdit && updates.roles !== undefined) {
          const originalRoles = (editingItem.roles || "").split(",").map((r) => r.trim());
          const newRoles = (Array.isArray(updates.roles) ? updates.roles.join(", ") : updates.roles || "").split(",").map((r) => r.trim());
          const hadAdmin = originalRoles.some((r) => r.toLowerCase() === "admin");
          const hasAdmin = newRoles.some((r) => r.toLowerCase() === "admin");
          if (hadAdmin && !hasAdmin) {
            showSnackbar("You cannot remove the 'admin' role from your own account.", "error");
            return;
          }
        }
        const finalRolesArray = ensureDefaultInRoles(
          Array.isArray(updates.roles)
            ? updates.roles.map((r) => String(r).trim()).filter(Boolean)
            : (updates.roles || "").split(",").map((r) => r.trim()).filter(Boolean)
        );
        if (updates.roles !== undefined) updates.roles = finalRolesArray.join(", ");
        const response = await lambdaFetch(`dynamo/${config.endpoint}/item/${primaryKeyValue}`, "put", credentials.user.accessToken, JSON.stringify(updates), "Item updated successfully", credentials);
        if (response?.success) {
          handleDialogClose();
          onRefresh();
          showSnackbar("User updated successfully");
        }
      }
    } catch (err) {
      console.error("Error saving user:", err);
      showSnackbar("Error saving user.", "error");
    }
  };

  const handleDeleteCancel = (event, reason) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
    setDeleteConfirmOpen(false);
    setItemPendingDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemPendingDelete) return;
    const currentUser = credentials.user;
    const isSelfDelete =
      (currentUser.username && itemPendingDelete.username && currentUser.username === itemPendingDelete.username) ||
      (currentUser.ID && itemPendingDelete.ID && currentUser.ID === itemPendingDelete.ID) ||
      (currentUser.id && itemPendingDelete.ID && currentUser.id === itemPendingDelete.ID) ||
      (currentUser.email && itemPendingDelete.username && currentUser.email === itemPendingDelete.username);
    if (isSelfDelete) {
      showSnackbar("You cannot delete your own user account.", "error");
      setDeleteConfirmOpen(false);
      setItemPendingDelete(null);
      return;
    }
    setDeleting(true);
    try {
      const config = USERS_CONFIG;
      const path = `dynamo/${config.endpoint}/item/${itemPendingDelete[config.primaryKey]}`;
      const response = await lambdaFetch(path, "delete", credentials.user.accessToken, null, "Item deleted successfully", credentials);
      if (response?.success) {
        onRefresh();
        showSnackbar("User deleted successfully");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      showSnackbar("Error deleting user.", "error");
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setItemPendingDelete(null);
    }
  };

  const handleBatchUploadOpen = () => {
    setBatchUploadRows([]);
    setBatchUploadErrors([]);
    setBatchUploadProgress({ current: 0, total: 0 });
    setBatchUploadOpen(true);
  };

  const handleDownloadTemplate = () => {
    // Columns match spreadsheet: Area, Username, Name, HR, unmasked, F–Q = role checkboxes (PAYROLL..admin), Roles (formula), data_role
    const header = "username,name,data_role,HR,unmasked,PAYROLL,AP,APBANK,AR,ASSET,CASH,GL,ACTIVITY,PO,default,all-access,admin,roles";
    // Roles column (U) formula: TEXTJOIN commas from F2:Q2 where cell = "X" (header in F$1:Q$1). In CSV the field is quoted and internal " escaped as ""
    const rolesFormula = '=TEXTJOIN(",",TRUE,IF(D2="X",D$1,""),IF(E2="X",E$1,""),IF(F2="X",F$1,""),IF(G2="X",G$1,""),IF(H2="X",H$1,""),IF(I2="X",I$1,""),IF(J2="X",J$1,""),IF(K2="X",K$1,""),IF(L2="X",L$1,""),IF(M2="X",M$1,""),IF(N2="X",N$1,""),IF(O2="X",O$1,""),IF(P2="X",P$1,""),IF(Q2="X",Q$1,""))';
    const formulaCsvField = `"${rolesFormula.replace(/"/g, '""')}"`;
    const example1 = `user@example.com,John Doe,all,,,,,,,,,,,,X,X,X,${formulaCsvField}`;
    const csv = [header, example1].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users_upload_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBatchUploadFile = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result ?? "";
      const { rows, errors } = parseBatchUploadCsv(text);
      setBatchUploadRows(rows);
      setBatchUploadErrors(errors);
    };
    reader.readAsText(file, "UTF-8");
    event.target.value = "";
  };

  const handleBatchUploadSubmit = async () => {
    const validRows = batchUploadRows.filter((r) => (r.username || "").trim());
    if (validRows.length === 0) {
      showSnackbar("No valid rows to upload (username required).", "error");
      return;
    }
    if (batchUploadErrors.length > 0) {
      showSnackbar(`Please fix validation errors before uploading (e.g. ${batchUploadErrors[0]})`, "warning");
      return;
    }
    const hasSuperAdmin = validRows.some((r) => {
      const rolesStr = (r.roles || "").trim();
      const roleList = rolesStr ? rolesStr.split(/[,;|]/).map((x) => x.trim().toLowerCase()) : [];
      return roleList.includes("super_admin");
    });
    if (hasSuperAdmin) {
      showSnackbar('Role "super_admin" is not allowed in batch upload. Remove it from the CSV and try again.', "error");
      return;
    }
    const config = USERS_CONFIG;
    setBatchUploading(true);
    setBatchUploadProgress({ current: 0, total: validRows.length });
    let created = 0;
    let failed = 0;
    try {
      await Promise.all([fetchAvailableAppRoles(), fetchAvailableDataRoles()]);
      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i];
        const username = (row.username || "").trim();
        const long_name = (row.long_name || row.name || "").trim() || username;
        let roles = (row.roles || "").trim();
        if (roles && !roles.includes(",")) roles = roles.replace(/[;|]/g, ",").trim();
        const rolesArray = ensureDefaultInRoles(
          roles ? roles.split(/[,;|]/).map((r) => r.trim()).filter(Boolean) : []
        );
        const data_role = (row.data_role || "").trim() || undefined;
        let active = row.active;
        if (active === undefined || active === "") active = true;
        if (typeof active === "string") active = /^(1|true|yes|active)$/i.test(active);
        const itemToCreate = { username, long_name, roles: rolesArray.join(", "), active: !!active };
        if (data_role) itemToCreate.data_role = data_role;
        itemToCreate[config.primaryKey] = `${config.idPrefix}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setBatchUploadProgress({ current: i + 1, total: validRows.length });
        try {
          await lambdaFetch(`dynamo/${config.endpoint}`, "post", credentials.user.accessToken, JSON.stringify(itemToCreate), "", credentials);
          created++;
        } catch (err) {
          failed++;
          console.error("Batch create failed for row:", row, err);
        }
      }
      showSnackbar(`Batch upload complete: ${created} created${failed ? `, ${failed} failed` : ""}.`, failed && created === 0 ? "error" : failed ? "warning" : "success");
      setBatchUploadOpen(false);
      onRefresh();
    } finally {
      setBatchUploading(false);
      setBatchUploadProgress({ current: 0, total: 0 });
    }
  };

  const tableData = useMemo(() => {
    const raw = usersData || [];
    return raw.map((row) => {
      const processed = { ...row };
      Object.keys(processed).forEach((k) => { if (processed[k] === null) processed[k] = ""; });
      return processed;
    });
  }, [usersData]);

  const columns = useMemo(() => {
    const config = USERS_CONFIG;
    const cols = config.customColumns.map((col) => {
      const colDef = {
        ...col,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (col.accessorKey === "active") {
            const isActive = value === 1 || value === "1" || value === true;
            return (
              <Chip
                label={isActive ? "Active" : "Inactive"}
                size="small"
                color={isActive ? "success" : undefined}
                sx={!isActive ? { bgcolor: "grey.400", color: "white" } : {}}
              />
            );
          }
          if (col.accessorKey === "roles") return value ? (Array.isArray(value) ? value.join(", ") : value) : "-";
          if (col.accessorKey === config.primaryKey) return <Chip label={value} size="small" color="primary" />;
          return value;
        },
      };
      if (col.accessorKey === "active") {
        colDef.filterFn = (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const val = row.getValue(columnId);
          const isActive = val === 1 || val === "1" || val === true;
          const label = isActive ? "active" : "inactive";
          return label.startsWith(filterValue.toLowerCase().trim());
        };
      }
      return colDef;
    });
    cols.push({
      accessorKey: "actions",
      header: "Actions",
      size: 120,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditClick(row.original)} color="primary">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDeleteClick(row.original)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    });
    return cols;
  }, []);

  const formatDeletePreview = (item) => {
    if (!item) return "";
    const { ID, username, long_name, roles, data_role, active } = item;
    return JSON.stringify({ ID, username, long_name, roles, data_role, active }, null, 2);
  };

  const renderFormFields = () => {
    const config = USERS_CONFIG;
    const fields = ["username", "long_name", "roles", "data_role", "active"];
    return fields.map((field) => {
      const value = currentItem[field] ?? "";
      const displayValue = typeof value === "object" ? JSON.stringify(value, null, 2) : value;
      if (field === "active") {
        const isActive = value === 1 || value === "1" || value === true;
        return (
          <Box key={field} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>ACTIVE</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant={isActive ? "contained" : "outlined"} color="success" size="small" onClick={() => handleInputChange(field, true)}>Active</Button>
              <Button variant={!isActive ? "contained" : "outlined"} size="small" onClick={() => handleInputChange(field, false)} sx={{ color: !isActive ? "white" : "text.secondary", bgcolor: !isActive ? "grey.600" : "transparent", borderColor: "grey.600" }}>Inactive</Button>
            </Box>
          </Box>
        );
      }
      if (field === "roles") {
        const rolesValue = ensureDefaultInRoles(normalizeRolesToArray(value));
        return (
          <Autocomplete
            key={field}
            multiple
            freeSolo
            disableCloseOnSelect
            options={availableAppRoles}
            value={rolesValue}
            onChange={(e, newValue) => handleInputChange(field, ensureDefaultInRoles(newValue))}
            isOptionEqualToValue={(option, val) =>
              String(option).trim().toLowerCase() === String(val).trim().toLowerCase()
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="APP ROLES"
                placeholder="Select or type app roles"
                helperText={
                  availableAppRoles.length > 0
                    ? `${availableAppRoles.length} available. All users must have the default role.`
                    : "No app roles found."
                }
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const tagProps = getTagProps({ index });
                const isDefaultRole =
                  String(option).trim().toLowerCase() === DEFAULT_APP_ROLE.toLowerCase();
                return (
                  <Chip
                    label={option}
                    {...tagProps}
                    size="small"
                    color="primary"
                    onDelete={isDefaultRole ? undefined : tagProps.onDelete}
                  />
                );
              })
            }
          />
        );
      }
      if (field === "data_role") {
        return (
          <Autocomplete
            key={field}
            freeSolo
            options={availableDataRoles}
            value={value || ""}
            onChange={(e, newValue) => handleInputChange(field, newValue)}
            renderInput={(params) => (
              <TextField {...params} margin="normal" label="DATA ROLE" placeholder={availableDataRoles.length > 0 ? "Select or type a data role" : "Loading..."} helperText={`${availableDataRoles.length} data roles available`} />
            )}
          />
        );
      }
      return (
        <TextField
          key={field}
          fullWidth
          margin="normal"
          label={field.replace(/_/g, " ").toUpperCase()}
          value={displayValue}
          onChange={(e) => handleInputChange(field, e.target.value)}
        />
      );
    });
  };

  const handleExportRows = (rows) => {
    csvExporter.generateCsv(rows.map((r) => r.original));
  };

  return (
    <>
      <Box sx={{ flex: 1, p: 2 }}>
        <MaterialReactTable
          columns={columns}
          data={tableData}
          initialState={{ showColumnFilters: true, density: "compact", columnPinning: { right: ["active", "actions"] } }}
          muiTableContainerProps={{ sx: { maxHeight: "600px" } }}
          enableStickyHeader
          enableFullScreenToggle={false}
          enableColumnOrdering={false}
          enableColumnDragging={false}
          enableColumnPinning
          enableHiding={false}
          enableColumnFilters
          enableSorting
          enablePagination={false}
          enableBottomToolbar={false}
          muiToolbarAlertBannerProps={error ? { color: "error", children: error } : undefined}
          state={{ isLoading: loading, showProgressBars: loading }}
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: "flex", gap: "1rem", p: "0.5rem", flexWrap: "wrap" }}>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddClick} variant="contained" color="primary">Add User</Button>
              <Button size="small" startIcon={<UploadFileIcon />} onClick={handleBatchUploadOpen} variant="outlined" color="primary">Batch upload users</Button>
              <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} variant="outlined">Refresh</Button>
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center", color: "text.secondary", ml: 1 }}>
                {tableData.length} record{tableData.length !== 1 ? "s" : ""} displayed
              </Typography>
              <IconButton onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)} title="Export to CSV">
                <CloudDownloadIcon />
              </IconButton>
            </Box>
          )}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth disableEscapeKeyDown>
        <DialogTitle>{dialogMode === "create" ? "Create New User" : "Edit User"}{editingItem && <Typography variant="caption" display="block" color="text.secondary">ID: {editingItem[USERS_CONFIG.primaryKey]}</Typography>}</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>{renderFormFields()}</Box></DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">{dialogMode === "create" ? "Create" : "Save"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={batchUploadOpen} onClose={() => !batchUploading && setBatchUploadOpen(false)} maxWidth="md" fullWidth disableEscapeKeyDown={batchUploading}>
        <DialogTitle>Batch upload users</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Upload a CSV with columns: <strong>username</strong>, <strong>name</strong>, <strong>roles</strong> (comma or semicolon separated), <strong>data_role</strong>. First row = headers.
          </DialogContentText>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" component="label" size="small" disabled={batchUploading}>
                Choose CSV file
                <input type="file" accept=".csv,.txt" hidden onChange={handleBatchUploadFile} />
              </Button>
              <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={handleDownloadTemplate} disabled={batchUploading}>
                Download template
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Template has username, name, role columns (PAYROLL through admin—put X for each role), roles (formula fills from Xs), and data_role. Save as CSV to upload.
            </Typography>
            {batchUploadErrors.length > 0 && (
              <Alert severity="warning">
                {batchUploadErrors.slice(0, 5).map((err, i) => <Typography key={i} variant="body2" component="div">{err}</Typography>)}
                {batchUploadErrors.length > 5 && <Typography variant="body2" color="text.secondary">… and {batchUploadErrors.length - 5} more</Typography>}
              </Alert>
            )}
            {batchUploadRows.length > 0 && (
              <>
                <Typography variant="subtitle2">Preview ({batchUploadRows.length} row{batchUploadRows.length !== 1 ? "s" : ""}, showing up to 500)</Typography>
                <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 4, borderBottom: "1px solid #ddd" }}>Name</th>
                        <th style={{ textAlign: "left", padding: 4, borderBottom: "1px solid #ddd" }}>Username</th>
                        <th style={{ textAlign: "left", padding: 4, borderBottom: "1px solid #ddd" }}>Roles</th>
                        <th style={{ textAlign: "left", padding: 4, borderBottom: "1px solid #ddd" }}>Data role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchUploadRows.slice(0, 500).map((row, i) => (
                        <tr key={i}>
                          <td style={{ padding: 4, borderBottom: "1px solid #eee" }}>{row.long_name || row.name || "—"}</td>
                          <td style={{ padding: 4, borderBottom: "1px solid #eee" }}>{row.username || "—"}</td>
                          <td style={{ padding: 4, borderBottom: "1px solid #eee" }}>{(row.roles || "").slice(0, 40)}{(row.roles || "").length > 40 ? "…" : ""}</td>
                          <td style={{ padding: 4, borderBottom: "1px solid #eee" }}>{row.data_role || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {batchUploadRows.length > 500 && <Typography variant="caption" color="text.secondary">… and {batchUploadRows.length - 500} more (only first 500 shown in preview)</Typography>}
                </Box>
              </>
            )}
            {batchUploading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Creating {batchUploadProgress.current} / {batchUploadProgress.total}…</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchUploadOpen(false)} disabled={batchUploading}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleBatchUploadSubmit} disabled={batchUploading || batchUploadRows.length === 0}>Upload {batchUploadRows.length > 0 ? `(${batchUploadRows.length})` : ""}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" gutterBottom>Are you sure you want to delete this user?</Typography>
            {itemPendingDelete && (
              <Box component="pre" sx={{ backgroundColor: "#f5f5f5", p: 2, borderRadius: 1, overflowX: "auto", fontSize: "0.85rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {formatDeletePreview(itemPendingDelete)}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deleting} startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {createPortal(
        <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} sx={{ zIndex: 1500 }}>
          <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ fontSize: "0.875rem" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>,
        document.body
      )}
    </>
  );
}
